import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Sparkles, X } from "lucide-react";
import { useSettings } from "@/lib/SettingsContext";

const DISMISS_KEY = "beautify-event-banner-dismissed";

const EventBanner = () => {
  const { event: EVENT } = useSettings();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      // Dismissal is stored per event.end_date so a fresh event brings the
      // banner back automatically.
      const stored = window.sessionStorage.getItem(DISMISS_KEY);
      return stored === (EVENT?.end_date || "");
    } catch {
      return false;
    }
  });

  // Reset dismissal when a new event (different end_date) appears
  useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem(DISMISS_KEY);
      setDismissed(stored === (EVENT?.end_date || ""));
    } catch {
      setDismissed(false);
    }
  }, [EVENT?.end_date]);

  if (!EVENT || !EVENT.active) return null;
  if (EVENT.end_date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(`${EVENT.end_date}T23:59:59`);
    if (isNaN(end.getTime()) || end.getTime() < today.getTime()) return null;
  }
  // Hide banner on the /contact page — user already there
  if (location.pathname.startsWith("/contact")) return null;
  if (dismissed) return null;

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, EVENT.end_date || "");
    } catch {
      /* noop */
    }
    setDismissed(true);
  };

  // On the Home page, use a hash so React Router picks it up + our
  // ScrollToTop helper smoothly scrolls to #event. On other pages, we
  // navigate to /#event and the same helper scrolls after route change.
  const isHome = location.pathname === "/";
  const scrollToEvent = (e) => {
    if (!isHome) return; // let router handle navigation to /#event
    e.preventDefault();
    const el = document.querySelector("#event");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      data-testid="event-top-banner"
      className="relative z-[60] bg-gradient-to-r from-plum-deep via-plum to-plum-light text-white"
    >
      <div className="absolute inset-0 bg-grain opacity-25 mix-blend-overlay pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-2.5 sm:py-3 flex items-center justify-center gap-3 sm:gap-4 flex-wrap">
        <span className="hidden sm:inline-flex items-center gap-1.5 text-champagne text-[10px] tracking-[0.3em] uppercase font-semibold shrink-0">
          <Sparkles size={12} /> {EVENT.eyebrow || "Special event"}
        </span>
        <p className="text-xs sm:text-sm text-center leading-snug min-w-0">
          <strong className="font-semibold">
            {EVENT.title || "Skincare event"}
          </strong>
          {EVENT.dates_label && (
            <>
              {" · "}
              <span className="text-blush">{EVENT.dates_label}</span>
            </>
          )}
          {EVENT.price_label && (
            <>
              {" · "}
              <span className="text-champagne font-semibold">
                {EVENT.price_label}
              </span>
            </>
          )}
        </p>
        <Link
          to="/#event"
          onClick={scrollToEvent}
          data-testid="event-top-banner-cta"
          className="inline-flex items-center gap-1 rounded-full bg-white text-plum-deep px-4 py-1.5 text-[11px] sm:text-xs font-semibold hover:bg-blush transition shrink-0"
        >
          See what's included
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss event banner"
          data-testid="event-top-banner-dismiss"
          className="ml-1 sm:absolute sm:right-3 sm:top-1/2 sm:-translate-y-1/2 rounded-full p-1 text-white/70 hover:text-white hover:bg-white/10 transition"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default EventBanner;
